import { useState, useEffect } from 'react';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader } from '../../components/ui';
import { LuChartColumn, LuCircleCheck, LuEye, LuFileText, LuPackage, LuPencil, LuStore, LuTrash2, LuWarehouse } from 'react-icons/lu';

import { fmt, fmtF } from '../../theme';
const SECTEURS = {
  boissons: { label: 'Boissons', couleur: '#EA580C', bg: '#FFF7ED' },
  restaurant: { label: 'Restaurant', couleur: '#0E7490', bg: '#ECFEFF' },
  vetements: { label: 'Vêtements', couleur: '#7C3AED', bg: '#F5F3FF' },
  cosmetiques: { label: 'Cosmétiques', couleur: '#D81B60', bg: '#FCE4EC' },
  gaz: { label: 'Gaz Butane', couleur: '#E65100', bg: '#FBE9E7' },
  general: { label: 'Général', couleur: '#455A64', bg: '#ECEFF1' }
};

export default function Stocks() {
  const [stocks, setStocks] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [vue, setVue] = useState('liste');
  const [stockSelectionne, setStockSelectionne] = useState(null);
  const [stockDetail, setStockDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const parPage = 6;

  const [form, setForm] = useState({
    nom: '',
    secteur_stock: 'boissons',
    description: '',
    etablissement_ids: []
  });

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    try {
      const [stocksRes, etabRes] = await Promise.all([
        API.get('/stocks'),
        API.get('/etablissements')
      ]);
      setStocks(stocksRes.data.stocks || []);
      setEtablissements(etabRes.data.etablissements || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const voirDetail = async (stock) => {
    try {
      const res = await API.get('/stocks/' + stock.id);
      setStockDetail(res.data);
      setVue('detail');
    } catch (err) {
      setMessage('❌ Erreur chargement détail');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (stockSelectionne) {
        await API.put('/stocks/' + stockSelectionne.id, form);
        setMessage('✅ Stock modifié avec succès !');
      } else {
        await API.post('/stocks', form);
        setMessage('✅ Stock créé avec succès !');
      }
      chargerDonnees();
      setVue('liste');
      setStockSelectionne(null);
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleModifier = (stock) => {
    setStockSelectionne(stock);
    setForm({
      nom: stock.nom || '',
      secteur_stock: stock.secteur_stock || 'boissons',
      description: stock.description || '',
      etablissement_ids: (stock.etablissements || []).map(e => e.id)
    });
    setVue('formulaire');
  };

  const handleDesactiver = async (id) => {
    if (!window.confirm('Désactiver ce stock ?')) return;
    try {
      await API.delete('/stocks/' + id);
      setMessage('✅ Stock désactivé !');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const toggleEtablissement = (id) => {
    const ids = form.etablissement_ids;
    if (ids.includes(id)) {
      setForm({ ...form, etablissement_ids: ids.filter(i => i !== id) });
    } else {
      setForm({ ...form, etablissement_ids: [...ids, id] });
    }
  };

  const resetForm = () => {
    setForm({ nom: '', secteur_stock: 'boissons', description: '', etablissement_ids: [] });
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    return SERVER_URL + '/uploads/' + logo.replace(/\\/g, '/').split('/').pop();
  };

  const etabCompatibles = etablissements.filter(
    e => e.secteur_stock === form.secteur_stock || e.secteur_stock === 'general'
  );

  const totalPages = Math.ceil(stocks.length / parPage);
  const stocksPage = stocks.slice((page - 1) * parPage, page * parPage);

  if (chargement) return <Loader />;

  return (
    <AppLayout role="admin">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Gestion des Stocks</h1>
            <p style={styles.headerSousTitre}>
              {stocks.length + ' stock(s) • Valeur totale : ' +
               fmtF(stocks.reduce((s, st) => s + (parseFloat(st.valeur_totale) || 0), 0))}
            </p>
          </div>
          {vue === 'liste' ? (
            <button onClick={() => { resetForm(); setStockSelectionne(null); setVue('formulaire'); }}
              style={styles.boutonAjouter}>
              + Nouveau Stock
            </button>
          ) : (
            <button onClick={() => { setVue('liste'); setStockDetail(null); }} style={styles.boutonRetour}>
              ← Retour à la liste
            </button>
          )}
        </div>

        {/* ── LISTE ── */}
        {vue === 'liste' && (
          <div>
            <div style={styles.section}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['Référence', 'Stock', 'Secteur', 'Établissements', 'Produits', 'Valeur estimée', 'Actions'].map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stocksPage.map((stock, i) => {
                    const secteur = SECTEURS[stock.secteur_stock] || SECTEURS.general;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                        <td style={styles.td}>
                          <span style={styles.refBadge}>{stock.reference}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: '#0F172A' }}>{stock.nom}</div>
                          {stock.description && (
                            <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>
                              {stock.description.substring(0, 40) + '...'}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 700,
                            background: secteur.bg, color: secteur.couleur
                          }}>
                            {secteur.label}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {(stock.etablissements || []).slice(0, 3).map((etab, j) => (
                              <div key={j} style={styles.etabBadge}>
                                {getLogoUrl(etab.logo) ? (
                                  <img src={getLogoUrl(etab.logo)} alt={etab.nom}
                                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <span style={{ fontSize: 14 }}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                                )}
                                <span style={{ fontSize: 11 }}>{etab.nom}</span>
                              </div>
                            ))}
                            {(stock.etablissements || []).length > 3 && (
                              <span style={{ fontSize: 11, color: '#64748B' }}>
                                {'+' + ((stock.etablissements || []).length - 3) + ' autres'}
                              </span>
                            )}
                            {(stock.etablissements || []).length === 0 && (
                              <span style={{ color: '#64748B', fontSize: 12 }}>Non attribué</span>
                            )}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 700, color: '#2563EB', fontSize: 16 }}>
                            {stock.nombre_produits || 0}
                          </span>
                          <span style={{ color: '#64748B', fontSize: 11 }}> produits</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 700, color: '#059669', fontSize: 15 }}>
                            {fmt(parseFloat(stock.valeur_totale || 0))}
                          </span>
                          <span style={{ color: '#64748B', fontSize: 11 }}> FCFA</span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => voirDetail(stock)} style={styles.btnVoir} aria-label="Voir le détail" title="Voir le détail"><LuEye aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                            <button onClick={() => handleModifier(stock)} style={styles.btnModifier} aria-label="Modifier" title="Modifier"><LuPencil aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                            <button onClick={() => handleDesactiver(stock.id)} style={styles.btnDesactiver} aria-label="Désactiver" title="Désactiver"><LuTrash2 aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {stocks.length === 0 && (
                <div style={styles.vide}>
                  <div style={{ fontSize: 48 }}><LuWarehouse aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Aucun stock créé</p>
                  <button onClick={() => setVue('formulaire')} style={styles.boutonAjouter}>
                    + Créer le premier stock
                  </button>
                </div>
              )}

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ ...styles.pagBtn, opacity: page === 1 ? 0.4 : 1 }}>←</button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)} style={{
                      ...styles.pagNum,
                      background: page === i + 1 ? 'var(--accent)' : 'white',
                      color: page === i + 1 ? 'white' : '#475569'
                    }}>{i + 1}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ ...styles.pagBtn, opacity: page === totalPages ? 0.4 : 1 }}>→</button>
                </div>
              )}
            </div>

            {/* RÉSUMÉ PAR SECTEUR */}
            <div style={{ marginTop: 24 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>
                <LuChartColumn aria-hidden style={{ verticalAlign: '-0.125em' }} /> Résumé par Secteur
              </h2>
              <div style={styles.carteGrid}>
                {Object.entries(SECTEURS).map(([secteur, config]) => {
                  const stocksSecteur = stocks.filter(s => s.secteur_stock === secteur);
                  if (stocksSecteur.length === 0) return null;
                  const valeur = stocksSecteur.reduce((s, st) => s + (parseFloat(st.valeur_totale) || 0), 0);
                  const produits = stocksSecteur.reduce((s, st) => s + (st.nombre_produits || 0), 0);
                  return (
                    <div key={secteur} style={{ ...styles.carteResume, borderTop: '4px solid ' + config.couleur }}>
                      <div style={{ fontSize: 24, marginBottom: 8, color: config.couleur }}><LuWarehouse aria-hidden /></div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#0F172A', marginBottom: 4 }}>
                        {config.label}
                      </div>
                      <div style={{ color: '#64748B', fontSize: 12, marginBottom: 12 }}>
                        {stocksSecteur.length + ' stock(s) • ' + produits + ' produits'}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 18, color: config.couleur }}>
                        {fmtF(valeur)}
                      </div>
                      <div style={{ color: '#64748B', fontSize: 11 }}>Valeur estimée</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── DÉTAIL STOCK ── */}
        {vue === 'detail' && stockDetail && (
          <div style={styles.detailCard}>
            <div style={styles.detailHeader}>
              <div>
                <span style={styles.refBadge}>{stockDetail.stock.reference}</span>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: '8px 0 4px' }}>
                  {stockDetail.stock.nom}
                </h2>
                <span style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                  background: (SECTEURS[stockDetail.stock.secteur_stock] || SECTEURS.general).bg,
                  color: (SECTEURS[stockDetail.stock.secteur_stock] || SECTEURS.general).couleur
                }}>
                  {(SECTEURS[stockDetail.stock.secteur_stock] || SECTEURS.general).label}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#059669' }}>
                  {fmtF(parseFloat(stockDetail.valeur_totale || 0))}
                </div>
                <div style={{ color: '#64748B', fontSize: 13 }}>Valeur totale estimée</div>
              </div>
            </div>

            {/* ÉTABLISSEMENTS */}
            <div style={styles.sectionDetail}>
              <h3 style={styles.sectionDetailTitre}>
                {'Établissements liés (' + (stockDetail.stock.etablissements || []).length + ')'}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {(stockDetail.stock.etablissements || []).map((etab, i) => (
                  <div key={i} style={styles.etabTag}>
                    {getLogoUrl(etab.logo) ? (
                      <img src={getLogoUrl(etab.logo)} alt={etab.nom}
                        style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 20 }}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{etab.nom}</span>
                    <span style={{ color: '#64748B', fontSize: 11 }}>{'(' + etab.type + ')'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* PRODUITS */}
            <div style={styles.sectionDetail}>
              <h3 style={styles.sectionDetailTitre}>
                {'Produits (' + stockDetail.produits.length + ')'}
              </h3>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['Établissement', 'Produit', 'Quantité', 'Prix unitaire', 'Valeur'].map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stockDetail.produits.map((produit, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {getLogoUrl(produit.etablissement_logo) ? (
                            <img src={getLogoUrl(produit.etablissement_logo)} alt={produit.etablissement_nom}
                              style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: 18 }}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                          )}
                          <span style={{ fontSize: 13 }}>{produit.etablissement_nom}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 700 }}>{produit.nom}</div>
                        {produit.code_barre && (
                          <div style={{ color: '#64748B', fontSize: 11 }}>{' ' + produit.code_barre}</div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          fontWeight: 700, fontSize: 15,
                          color: produit.quantite === 0 ? '#DC2626'
                            : produit.quantite <= produit.seuil_alerte ? '#EA580C' : '#059669'
                        }}>
                          {produit.quantite}
                        </span>
                        <span style={{ color: '#64748B', fontSize: 11 }}>{' ' + produit.unite}</span>
                      </td>
                      <td style={styles.td}>
                        {fmtF(parseInt(produit.prix))}
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 700, color: '#059669' }}>
                          {fmtF(parseFloat(produit.valeur || 0))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {stockDetail.produits.length === 0 && (
                <div style={styles.vide}>
                  <p>Aucun produit dans ce stock</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── FORMULAIRE ── */}
        {vue === 'formulaire' && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitre}>
              {stockSelectionne ? 'Modifier le stock' : 'Nouveau Stock'}
            </h2>

            <form onSubmit={handleSubmit}>

              {/* SECTION 1 — INFOS */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuWarehouse aria-hidden style={{ verticalAlign: '-0.125em' }} /> Informations du Stock</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuFileText aria-hidden style={{ verticalAlign: '-0.125em' }} /> Nom du stock *</label>
                    <input
                      type="text"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Stock Boissons Principal"
                      required
                    />
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Secteur de stock *</label>
                    <select
                      value={form.secteur_stock}
                      onChange={(e) => setForm({ ...form, secteur_stock: e.target.value, etablissement_ids: [] })}
                      style={styles.select}
                      disabled={!!stockSelectionne}
                    >
                      {Object.entries(SECTEURS).map(([val, cfg]) => (
                        <option key={val} value={val}>{cfg.label}</option>
                      ))}
                    </select>
                    {stockSelectionne && (
                      <div style={{ color: '#64748B', fontSize: 11, marginTop: 4 }}>
                        Le secteur ne peut pas être modifié après création
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.referenceInfo}>
                  {stockSelectionne
                    ? ('Référence : ' + stockSelectionne.reference)
                    : 'La référence sera générée automatiquement (ex: STK-26-0001)'}
                </div>

                <div style={{ marginTop: 16 }}>
                  <label style={styles.label}><LuFileText aria-hidden style={{ verticalAlign: '-0.125em' }} /> Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    style={{ ...styles.input, height: 80, resize: 'vertical', marginTop: 6 }}
                    placeholder="Description du stock..."
                  />
                </div>
              </div>

              {/* SECTION 2 — ÉTABLISSEMENTS */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}>
                  <LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Attribuer aux Établissements
                  <span style={styles.compteurBadge}>
                    {form.etablissement_ids.length + ' sélectionné(s)'}
                  </span>
                </div>

                {etabCompatibles.length === 0 ? (
                  <div style={{ color: '#64748B', fontSize: 13 }}>
                    Aucun établissement compatible avec ce secteur.
                  </div>
                ) : (
                  <div style={styles.etabGrid}>
                    {etabCompatibles.map((etab, i) => {
                      const selectionne = form.etablissement_ids.includes(etab.id);
                      return (
                        <div
                          key={i}
                          onClick={() => toggleEtablissement(etab.id)}
                          style={{
                            ...styles.etabChoix,
                            border: selectionne ? '2px solid #059669' : '2px solid #E2E8F0',
                            background: selectionne ? '#F0FFF4' : 'white'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {getLogoUrl(etab.logo) ? (
                              <img src={getLogoUrl(etab.logo)} alt={etab.nom}
                                style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                              <div style={styles.etabChoixAvatar}>{etab.nom[0]}</div>
                            )}
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>{etab.nom}</div>
                              <div style={{ color: '#64748B', fontSize: 11 }}>{etab.type}</div>
                            </div>
                          </div>
                          {selectionne && <span style={{ color: '#059669', fontWeight: 700 }}><LuCircleCheck aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ACTIONS */}
              <div style={styles.formActions}>
                <button type="button" onClick={() => setVue('liste')} style={styles.btnAnnuler}>
                  Annuler
                </button>
                <button type="submit" style={styles.btnSauvegarder}>
                  {stockSelectionne ? 'Sauvegarder' : 'Créer le stock'}
                </button>
              </div>

            </form>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', height: '100vh',
    fontSize: 24, color: '#475569'
  },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  section: S.section,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
  refBadge: {
    background: '#EFF6FF', color: '#2563EB',
    padding: '4px 10px', borderRadius: 8,
    fontSize: 12, fontWeight: 700, fontFamily: 'monospace'
  },
  etabBadge: {
    display: 'flex', alignItems: 'center',
    gap: 4, background: '#F8FAFC', padding: '3px 8px', borderRadius: 8
  },
  btnVoir: S.btnVoir,
  btnModifier: S.btnModifier,
  btnDesactiver: S.btnDesactiver,
  vide: S.vide,
  pagination: { ...S.pagination, marginTop: 16 },
  pagBtn: S.pagBtn,
  pagNum: { ...S.pagNum, width: 30 },
  carteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16
  },
  carteResume: {
    background: 'white', borderRadius: 12,
    padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center'
  },
  detailCard: {
    background: 'white', borderRadius: 16,
    padding: 32, boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  detailHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 24,
    paddingBottom: 20, borderBottom: '1px solid #F1F5F9'
  },
  sectionDetail: {
    marginBottom: 24, padding: 20,
    background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0'
  },
  sectionDetailTitre: { fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 16 },
  etabTag: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'white', padding: '8px 14px',
    borderRadius: 10, border: '1px solid #E2E8F0'
  },
  formCard: S.formCard,
  formTitre: { ...S.formTitre, marginBottom: 24 },
  sectionForm: { ...S.sectionForm, marginBottom: 24 },
  sectionFormTitre: { ...S.sectionFormTitre, marginBottom: 16 },
  formGrid: S.formGrid,
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  referenceInfo: {
    marginTop: 12, padding: '10px 14px',
    background: '#EFF6FF', borderRadius: 8, color: '#2563EB', fontSize: 13
  },
  compteurBadge: {
    background: '#EFF6FF', color: '#2563EB',
    padding: '2px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 700, marginLeft: 8
  },
  etabGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  etabChoix: {
    padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  etabChoixAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2563EB, #00B0FF)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 700, fontSize: 16
  },
  formActions: { ...S.formActions, marginTop: 16 },
  btnAnnuler: S.btnAnnuler,
  btnSauvegarder: S.btnSauvegarder
};